import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUploader from "@/components/PhotoUploader";
import { Info } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  "GP", "Nurse", "Consultant", "Physiotherapist", "Occupational Therapist",
  "Speech Therapist", "Pharmacist", "Psychiatrist", "Psychologist", "Dentist",
  "Social Worker", "Care Coordinator", "Support Worker", "Local Authority",
  "Advocate", "Solicitor", "IMCA",
  "Family", "Friend", "Next of Kin", "Emergency Contact",
  "Professional", "Other",
];

const TITLE_OPTIONS = ["", "Mr", "Mrs", "Miss", "Ms", "Mx", "Dr", "Prof", "Rev"];
const PREFERRED_CONTACT_OPTIONS = ["any", "phone", "email", "sms"];

function composeName(v) {
  const parts = [v.title, v.first_name, v.last_name].filter(Boolean).map((s) => String(s).trim()).filter(Boolean);
  return parts.length ? parts.join(" ") : (v.name || "").trim();
}

export default function ImportantPersonDialog({ open, onOpenChange, record, onSubmit, onPhotoUpdated }) {
  const isEdit = !!record?.id;
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Seed defaults from record OR sensibly split legacy `name` into first/last
    let firstName = record?.first_name || "";
    let lastName = record?.last_name || "";
    if (!firstName && !lastName && record?.name) {
      const parts = String(record.name).trim().split(/\s+/);
      if (parts.length === 1) {
        firstName = parts[0];
      } else {
        firstName = parts[0];
        lastName = parts.slice(1).join(" ");
      }
    }
    setValues({
      title: record?.title || "",
      first_name: firstName,
      last_name: lastName,
      role: record?.role || "",
      relationship: record?.relationship || "",
      organization: record?.organization || "",
      phone: record?.phone || "",
      secondary_phone: record?.secondary_phone || "",
      email: record?.email || "",
      address: record?.address || "",
      preferred_contact_method: record?.preferred_contact_method || "any",
      is_primary_contact: !!record?.is_primary_contact,
      is_next_of_kin: !!record?.is_next_of_kin,
      is_emergency_contact: !!record?.is_emergency_contact,
      notes: record?.notes || "",
    });
  }, [open, record]);

  const change = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.role) return toast.error("Role is required");
    const name = composeName(values);
    if (!name) return toast.error("Enter a first or last name");
    setSubmitting(true);
    try {
      await onSubmit({ ...values, name });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="ip-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Important Person" : "New Important Person"}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? "Update contact details" : "Add a family member, professional or other important contact"}
          </DialogDescription>
        </DialogHeader>

        {/* Photo section */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          {isEdit ? (
            <PhotoUploader
              kind="important_person"
              targetId={record.id}
              person={record}
              size="xl"
              authed
              onUpdated={(patch) => onPhotoUpdated?.(patch)}
            />
          ) : (
            <div className="flex items-start gap-2 text-[11px] text-slate-600" data-testid="ip-photo-hint">
              <Info className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
              <span>Save this contact first, then reopen to add a profile photo.</span>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          {/* Identity */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Title</Label>
            <select
              value={values.title || ""}
              onChange={(e) => change("title", e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
              data-testid="ip-field-title"
            >
              {TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t || "—"}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">
              Role<span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Select value={values.role || ""} onValueChange={(v) => change("role", v)}>
              <SelectTrigger data-testid="ip-field-role" className="h-9 text-sm bg-white">
                <SelectValue placeholder="Select role…" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {ROLE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">First Name</Label>
            <Input value={values.first_name || ""} onChange={(e) => change("first_name", e.target.value)} className="h-9 text-sm bg-white" data-testid="ip-field-first-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Last Name</Label>
            <Input value={values.last_name || ""} onChange={(e) => change("last_name", e.target.value)} className="h-9 text-sm bg-white" data-testid="ip-field-last-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Relationship</Label>
            <Input value={values.relationship || ""} onChange={(e) => change("relationship", e.target.value)} placeholder="e.g. General Practitioner, Mother" className="h-9 text-sm bg-white" data-testid="ip-field-relationship" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Organisation / Practice</Label>
            <Input value={values.organization || ""} onChange={(e) => change("organization", e.target.value)} className="h-9 text-sm bg-white" data-testid="ip-field-organization" />
          </div>

          {/* Contact */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Primary Phone</Label>
            <Input value={values.phone || ""} onChange={(e) => change("phone", e.target.value)} className="h-9 text-sm bg-white" data-testid="ip-field-phone" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Secondary Phone</Label>
            <Input value={values.secondary_phone || ""} onChange={(e) => change("secondary_phone", e.target.value)} className="h-9 text-sm bg-white" data-testid="ip-field-secondary-phone" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Email</Label>
            <Input type="email" value={values.email || ""} onChange={(e) => change("email", e.target.value)} className="h-9 text-sm bg-white" data-testid="ip-field-email" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-700">Preferred Contact Method</Label>
            <Select value={values.preferred_contact_method || "any"} onValueChange={(v) => change("preferred_contact_method", v)}>
              <SelectTrigger data-testid="ip-field-preferred" className="h-9 text-sm bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {PREFERRED_CONTACT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs font-medium text-slate-700">Address</Label>
            <Textarea rows={2} value={values.address || ""} onChange={(e) => change("address", e.target.value)} className="text-sm bg-white" data-testid="ip-field-address" />
          </div>

          {/* Flags */}
          <div className="sm:col-span-2 flex flex-wrap gap-2 pt-1">
            <FlagToggle label="Primary Contact" testid="ip-flag-primary" value={values.is_primary_contact} onChange={(v) => change("is_primary_contact", v)} />
            <FlagToggle label="Next of Kin" testid="ip-flag-nok" value={values.is_next_of_kin} onChange={(v) => change("is_next_of_kin", v)} />
            <FlagToggle label="Emergency Contact" testid="ip-flag-emergency" value={values.is_emergency_contact} onChange={(v) => change("is_emergency_contact", v)} />
          </div>

          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs font-medium text-slate-700">Notes</Label>
            <Textarea rows={3} value={values.notes || ""} onChange={(e) => change("notes", e.target.value)} className="text-sm bg-white" data-testid="ip-field-notes" />
          </div>

          <DialogFooter className="sm:col-span-2 mt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="ip-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-sky-700 hover:bg-sky-800" data-testid="ip-submit">
              {submitting ? "Saving…" : isEdit ? "Update Contact" : "Create Contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FlagToggle({ label, value, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={testid}
      aria-pressed={value}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        value
          ? "bg-sky-700 text-white border-sky-700"
          : "bg-white text-slate-700 border-slate-300 hover:border-sky-500"
      }`}
    >
      {label}
    </button>
  );
}
